import { useCallback, useEffect, useState } from 'react';

import * as storage from '@/utils/storage';

import useDidUpdate from './useDidUpdate';

type UseStorageToggle = (
  storageKey: string,
  defaultValue?: boolean,
  subscribe?: boolean,
) => [boolean, VoidFunction, React.Dispatch<React.SetStateAction<boolean>>];

const useStorageToggle: UseStorageToggle = (
  storageKey,
  defaultValue = true,
  subscribe = false,
) => {
  const [isOpened, setIsOpened] = useState(
    storage.get(storageKey) ?? defaultValue,
  );

  const toggleIsOpened = useCallback(() => setIsOpened((prev) => !prev), []);

  useDidUpdate(() => {
    storage.set(storageKey, isOpened);
  }, [isOpened, storageKey]);

  useEffect(() => {
    if (!subscribe) {
      return;
    }

    const cb = (event: StorageEvent) => {
      if (event.key !== storageKey) {
        return;
      }

      setIsOpened(event.newValue === 'true');
    };

    window.addEventListener('storage', cb);

    return () => window.removeEventListener('storage', cb);
  }, [subscribe, storageKey]);

  return [isOpened, toggleIsOpened, setIsOpened];
};

export default useStorageToggle;
